import type { CallFoundryDexie } from '../db';

const LOCAL_LEARNER_ID_KEY = 'callfoundry_local_learner_id';
const LEGACY_USER_ID_KEY = 'callfoundry_user_id';
const AUTH_USER_ID_KEY = 'callfoundry_auth_uid';
export const LEGACY_ANONYMOUS_USER_ID = 'local-anonymous';

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const memoryStorage = new Map<string, string>();

function getStorageItem(key: string): string | null {
  try {
    if (typeof localStorage !== 'undefined' && localStorage !== null) {
      return localStorage.getItem(key);
    }
  } catch {
    // fallback
  }
  return memoryStorage.get(key) ?? null;
}

function setStorageItem(key: string, value: string): void {
  try {
    if (typeof localStorage !== 'undefined' && localStorage !== null) {
      localStorage.setItem(key, value);
      return;
    }
  } catch {
    // fallback
  }
  memoryStorage.set(key, value);
}

function removeStorageItem(key: string): void {
  try {
    if (typeof localStorage !== 'undefined' && localStorage !== null) {
      localStorage.removeItem(key);
      return;
    }
  } catch {
    // fallback
  }
  memoryStorage.delete(key);
}

/**
 * Generates or retrieves the persistent unique local learner ID for this installation.
 * Format: `loc_<uuid>`.
 * This ID is immutable and unique to this local browser profile.
 */
export function getLocalLearnerId(): string {
  let localId = getStorageItem(LOCAL_LEARNER_ID_KEY);
  if (!localId) {
    // Check if legacy key exists and is NOT 'local-anonymous'
    const legacyId = getStorageItem(LEGACY_USER_ID_KEY);
    if (legacyId && legacyId !== LEGACY_ANONYMOUS_USER_ID && !legacyId.startsWith('loc_')) {
      localId = `loc_${legacyId}`;
    } else {
      localId = `loc_${generateUUID()}`;
    }
    setStorageItem(LOCAL_LEARNER_ID_KEY, localId);
  }
  return localId;
}

/**
 * Returns the currently active user ID:
 * - If signed in to a Firebase account: returns the account UID.
 * - Otherwise: returns the local installation's unique learner ID (`loc_<uuid>`).
 */
export function getActiveUserId(): string {
  const authUid = getStorageItem(AUTH_USER_ID_KEY);
  if (authUid && authUid.trim().length > 0) {
    return authUid;
  }
  return getLocalLearnerId();
}

/**
 * Backwards compatibility alias for getActiveUserId()
 */
export function getCurrentUserId(): string {
  return getActiveUserId();
}

/**
 * Updates the authenticated account UID (upon login or logout).
 */
export function setAuthenticatedUserId(authUid: string | null): void {
  if (authUid) {
    setStorageItem(AUTH_USER_ID_KEY, authUid);
  } else {
    removeStorageItem(AUTH_USER_ID_KEY);
  }
}

/**
 * Backwards-compatibility setter
 */
export function setCurrentUserId(userId: string): void {
  if (userId.startsWith('loc_') || userId === LEGACY_ANONYMOUS_USER_ID) {
    setAuthenticatedUserId(null);
  } else {
    setAuthenticatedUserId(userId);
  }
}

/**
 * Returns true if the given or current user is operating as a local learner without cloud sync.
 */
export function isLocalLearner(userId?: string): boolean {
  const targetId = userId ?? getActiveUserId();
  return targetId.startsWith('loc_') || targetId === LEGACY_ANONYMOUS_USER_ID;
}

export function isLocalOnlyUser(): boolean {
  return isLocalLearner();
}

/**
 * Clears authenticated identity while preserving the persistent local learner ID.
 */
export function resetUserIdentity(): void {
  setAuthenticatedUserId(null);
}

/**
 * Resets identity to a completely fresh local learner ID for multi-user session isolation.
 */
export function resetToFreshLocalLearner(): string {
  setAuthenticatedUserId(null);
  const freshId = `loc_${generateUUID()}`;
  setStorageItem(LOCAL_LEARNER_ID_KEY, freshId);
  return freshId;
}

/**
 * Resets local identity completely (for test isolation only).
 */
export function resetLocalLearnerIdentityForTesting(): void {
  removeStorageItem(LOCAL_LEARNER_ID_KEY);
  removeStorageItem(LEGACY_USER_ID_KEY);
  removeStorageItem(AUTH_USER_ID_KEY);
  memoryStorage.clear();
}

/**
 * Migrates any legacy records using 'local-anonymous' to this installation's unique localLearnerId.
 */
export async function migrateLegacyAnonymousIdentity(database: CallFoundryDexie): Promise<number> {
  const localLearnerId = getLocalLearnerId();
  let migratedCount = 0;

  await database.transaction('rw', [database.events, database.syncQueue], async () => {
    const legacyEvents = await database.events
      .filter(
        (evt) =>
          evt.originLearnerId === LEGACY_ANONYMOUS_USER_ID ||
          (evt as Record<string, unknown>).userId === LEGACY_ANONYMOUS_USER_ID
      )
      .toArray();

    for (const evt of legacyEvents) {
      await database.events.update(evt.eventId, {
        originLearnerId: localLearnerId,
      });
      migratedCount++;
    }

    const legacyQueue = await database.syncQueue
      .filter(
        (q) =>
          q.userId === LEGACY_ANONYMOUS_USER_ID ||
          q.accountUid === LEGACY_ANONYMOUS_USER_ID
      )
      .toArray();

    for (const q of legacyQueue) {
      await database.syncQueue.update(q.id, {
        userId: localLearnerId,
      });
    }
  });

  return migratedCount;
}
